// Brand creation step in the onboarding wizard
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Database } from '@/database.types'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { AutoAwesome } from '@mui/icons-material'
import fieldMetadata from '@/lib/fieldMetadata.json'
import { ChevronLeft, ChevronRight, Sparkles, AlertCircle, Lightbulb, CheckCircle, User, Target, Globe, MessageSquare, Palette, Users } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import AIGenerateDialog from '@/components/brand/AIGenerateDialog'
import { RainbowButton } from '@/components/ui/rainbow-button'
import { ButtonGroup, ButtonGroupItem } from '@/components/ui/button-group'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/progress'

interface BrandWizardStepProps {
  onComplete: () => void
  onPrevious: () => void
  existingBrandId: string | null
  onBrandUpdate: (data: Partial<Brand>) => void
  skipBilling?: boolean
}

type FieldMetadata = {
  hidden: boolean
  description: string
  example: string | number
  bestPractices: string
  group: string
  inputControl: string
  options: string[]
  keyField: boolean
  allowAiGeneration: boolean
  howItsUsed: string
  name: string
}

type Brand = Database['public']['Tables']['brands']['Row']

export function BrandWizardStep({ onComplete, onPrevious, existingBrandId, onBrandUpdate, skipBilling }: BrandWizardStepProps) {
  const [isSubmitting] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [keyFields, setKeyFields] = useState<[string, FieldMetadata][]>([])
  const [brandId, setBrandId] = useState<string>('')
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [currentAIField, setCurrentAIField] = useState<keyof Brand>('brand_name');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [fieldValues, setFieldValues] = useState<Partial<Brand>>({});
  const [isFeedbackMode, setIsFeedbackMode] = useState(false);
  const [brandGeniusFeedback, setBrandGeniusFeedback] = useState('');
  const [fieldError, setFieldError] = useState<string>('');
  const [isShaking, setIsShaking] = useState(false);
  
  const { register, setValue, watch } = useForm<any>({
    defaultValues: {},
    mode: 'onChange'
  })

  // // Create debounced update function for the current field only
  // const debouncedUpdate = useDebouncedCallback(
  //   (key: keyof Brand, value: any) => {
  //     if (value !== undefined && value !== null && value !== '') {
  //       // Update local state first
  //       setFieldValues(prev => ({
  //         ...prev,
  //         [key]: value
  //       }));
  //       // Then notify parent
  //       const updateData = { 
  //         ...fieldValues, // Include all previous values
  //         [key]: value    // Plus the new value
  //       } as Partial<Brand>;
  //       onBrandUpdate(updateData);
  //     }
  //   },
  //   300 // 300ms delay
  // );

  // Watch current field for changes
  const currentField = keyFields[currentStep];
  const currentFieldKey = currentField ? currentField[0] as keyof Brand : null;
  const currentFieldValue = currentFieldKey ? watch(currentFieldKey) : null;
  
  // DEBUG: Log current field state
  useEffect(() => {
    if (currentFieldKey && keyFields.length > 0) {
      console.log(`[DEBUG BrandWizardStep] Current field: ${currentFieldKey}, Value: ${currentFieldValue || '(empty)'}`);
      console.log(`[DEBUG BrandWizardStep] Current step: ${currentStep}/${keyFields.length - 1}`);
    }
  }, [currentFieldKey, currentFieldValue, currentStep]);

  // Update brand data when current field changes
  useEffect(() => {
    if (currentFieldKey && currentFieldValue !== undefined && currentFieldValue !== null) {
      // Update with current field value while preserving others
      const updatedValues = { 
        ...fieldValues,
        [currentFieldKey]: currentFieldValue 
      };
      setFieldValues(updatedValues);
      
      // For the preview, only include fields up to the current step
      const visibleFields = keyFields
        .slice(0, currentStep + 1)
        .reduce((acc, [fieldName]) => {
          if (fieldName === currentFieldKey) {
            // For current field, use the new value
            acc[fieldName as keyof Brand] = currentFieldValue as any;
          } else {
            // For previous fields, use stored values
            const value = fieldValues[fieldName as keyof Brand];
            if (value !== undefined && value !== null && value !== '') {
              acc[fieldName as keyof Brand] = value as any;
            }
          }
          return acc;
        }, {} as Partial<Brand>);
      
      // ADD DEBUGGING AND PROTECTION: Only send brand updates if they include more than just text field changes
      // This prevents text field edits from causing circular updates
      console.log(`Field ${currentFieldKey} changed to:`, typeof currentFieldValue === 'string' ? 
        (currentFieldValue.length > 20 ? 
          currentFieldValue.substring(0, 20) + '...' : 
          currentFieldValue) : 
        currentFieldValue);
      
      // Explicitly make sure we're not sending an ID with form field updates
      // This prevents form field edits from causing brand ID changes
      delete visibleFields.id;
      
      // Notify parent of visible fields only - with protective measures
      onBrandUpdate(visibleFields);
    }
  }, [currentFieldValue, currentFieldKey]);

  // Initialize key fields
  useEffect(() => {
    const fields = Object.entries(fieldMetadata)
      .filter(([_, meta]) => meta.keyField && !meta.hidden)
      .sort((a, b) => a[1].group.localeCompare(b[1].group))
    setKeyFields(fields)
  }, [])

  // Initialize brand
  useEffect(() => {
    // When existingBrandId changes, reset initialization status 
    // This ensures we reinitialize with the new brand ID
    if (existingBrandId && isInitialized) {
      console.log('existingBrandId changed to:', existingBrandId);
      console.log('Current brandId in component state:', brandId);
      
      // CRITICAL FIX: To prevent reinitializing during text field edits, only reinitialize
      // if the brand ID has actually changed, not just because of a text field edit
      if (existingBrandId === brandId) {
        console.log('existingBrandId matches current brandId - skipping reinitialization');
        return;
      }
      
      // Check if we're actively editing a field - if so, don't reset
      // This prevents text field edits from causing reinitialization
      const currentFieldName = keyFields[currentStep]?.[0];
      const currentFieldValue = currentFieldName ? watch(currentFieldName as any) : null;
      if (currentFieldValue !== undefined && currentFieldValue !== null && currentFieldValue !== '') {
        console.log('Active field edit detected - preserving current state and skipping reinitialization');
        return;
      }
      
      console.log('Resetting initialization status to reinitialize with new ID');
      setIsInitialized(false);
      setIsInitializing(false);
      return;
    }
    
    const initializeBrand = async () => {
      // Prevent multiple initialization attempts
      if (isInitializing || isInitialized) return;
      setIsInitializing(true);

      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return;

        let brand;

        // If we have an explicit brand ID, use that
        if (existingBrandId) {
          console.log('Using existing brand ID from props:', existingBrandId);
          
          // First check if this is a web-scraped brand
          const { data: brandData, error } = await supabase
            .from('brands')
            .select('*, web_site_scraping_status')
            .eq('id', existingBrandId)
            .single();

          if (error) {
            console.error('Error loading brand:', error);
            return;
          }
          
          if (brandData.web_site_scraping_status === 'COMPLETED') {
            console.log('This is a web-scraped brand with completed scraping');
          }
          
          console.log('Loaded brand details:', {
            id: brandData.id,
            name: brandData.brand_name,
            url: brandData.url,
            scrape_status: brandData.web_site_scraping_status,
            status: brandData.status
          });
          
          // DEBUG: Check all brand fields to understand the issue
          console.log('[DEBUG] Full brand data loaded:', brandData);
          
          brand = brandData;
        } else {
          // First, get the user's accounts
          const { data: accounts, error: accountError } = await supabase
            .schema('basejump')
            .rpc('get_accounts_with_role');
          
          if (accountError || !accounts || accounts.length === 0) {
            console.error('Error fetching user accounts:', accountError);
            return;
          }

          // Check all brands for the user's accounts (both ACTIVE and DRAFT)
          // to prevent creating duplicate brands after web scraping
          const { data: allBrands } = await supabase
            .from('brands')
            .select('*')
            .in('account_id', accounts);
            
          // Log all found brands to help debugging
          console.log('Found brands for accounts:', allBrands?.map(b => ({
            id: b.id,
            status: b.status,
            scrape_status: b.web_site_scraping_status,
            name: b.brand_name,
            updated_at: b.updated_at
          })));
            
          if (allBrands && allBrands.length > 0) {
            // First, check if there's a brand with completed website scraping
            // This is important because web scraped brands should be prioritized
            const scrapedBrand = allBrands.find(b => 
              b.web_site_scraping_status === 'COMPLETED');
              
            if (scrapedBrand) {
              console.log('Using web-scraped brand with ID:', scrapedBrand.id);
              brand = scrapedBrand;
            }
            // Otherwise, if no scraped brand is found, prefer DRAFT over ACTIVE if available
            else {
              const draftBrand = allBrands.find(b => b.status === 'DRAFT');
              if (draftBrand) {
                console.log('Using existing DRAFT brand:', draftBrand.id);
                brand = draftBrand;
              } else {
                // Otherwise use the most recently updated brand
                const sortedBrands = [...allBrands].sort((a, b) => 
                  new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime()
                );
                console.log('Using most recent brand:', sortedBrands[0].id);
                brand = sortedBrands[0];
              }
            }
          } else {
            // Create new brand only if none exist
            console.log('No existing brands found, creating new brand');
            
            // The personal account ID should be the same as the user ID (based on basejump setup)
            // But first let's verify the user has access to their account
            const { data: accounts, error: accountError } = await supabase
              .schema('basejump')
              .rpc('get_accounts_with_role');
            
            if (accountError || !accounts || accounts.length === 0) {
              console.error('Error fetching user accounts:', accountError);
              console.error('User does not have any accounts. This should not happen.');
              console.error('User ID:', session.user.id);
              console.error('Accounts returned:', accounts);
              
              // Let's try to see if the personal account exists at all
              const { data: personalAccountCheck, error: checkError } = await supabase
                .schema('basejump')
                .from('accounts')
                .select('*')
                .eq('id', session.user.id)
                .eq('personal_account', true);
              
              console.error('Personal account check:', personalAccountCheck, checkError);
              return;
            }
            
            // Use the first account (should be personal account)
            const accountId = accounts[0];
            console.log('Using account ID:', accountId);

            const newBrand = {
              account_id: accountId,
              status: 'DRAFT',
              id: crypto.randomUUID(),
              brand_name: '',
              idea: '',
              url: '',
              default_landing_page: '', // Set default_landing_page to match url (empty initially)
              priority: 100, // Set initial high priority for new brands
              convergence_check_frequency: '1 hour' // Start with frequent checks
            } as const;

            const { data, error } = await supabase
              .from('brands')
              .insert(newBrand)
              .select()
              .single();

            if (error) {
              if (error.code === '23505') { // Unique constraint violation
                // Someone else created a draft in the meantime, fetch it
                const { data: latestDraft } = await supabase
                  .from('brands')
                  .select('*')
                  .eq('status', 'DRAFT')
                  .eq('account_id', accountId)
                  .single();
                
                brand = latestDraft;
              } else {
                console.error('Error creating brand:', error);
                console.error('Error details:', error);
                return;
              }
            } else {
              brand = data;
            }
          }
        }

        if (brand) {
          setBrandId(brand.id);
          // CRITICAL: Initialize fieldValues with ALL brand data
          setFieldValues(brand);
          console.log('[DEBUG] Initialized fieldValues with brand data');
          
          // Set form values for all fields, even if empty
          Object.keys(fieldMetadata).forEach((key) => {
            const value = brand[key as keyof Brand] || '';
            setValue(key as keyof Brand, value);
            
            // DEBUG: Log field mapping
            if (key === 'brand_name' || key === 'url' || key === 'elevator_pitch' || key === 'target_market') {
              console.log(`[DEBUG] Setting form field '${key}' to value:`, value);
            }
          });
          
          // DEBUG: Verify form values were set
          setTimeout(() => {
            console.log('[DEBUG] Verifying form values after setting:');
            ['brand_name', 'elevator_pitch', 'target_market', 'url'].forEach(field => {
              const formValue = watch(field);
              console.log(`  - ${field}: ${formValue || '(empty)'}`);
            });
          }, 100);
          
          // DEBUG: Log what we're sending to parent
          console.log('[DEBUG] Sending FULL brand data to parent:', {
            id: brand.id,
            brand_name: brand.brand_name,
            url: brand.url,
            elevator_pitch: brand.elevator_pitch?.substring(0, 50) + '...',
            target_market: brand.target_market?.substring(0, 50) + '...'
          });
          
          // CRITICAL FIX: Send ALL brand data to parent when initially loading
          // This ensures the brand preview shows all data, not just fields up to current step
          onBrandUpdate(brand);
          setIsInitialized(true);
        }
      } finally {
        setIsInitializing(false);
      }
    };

    initializeBrand();
  }, [existingBrandId, fieldMetadata, setValue, onBrandUpdate]);

  // Handle next step
  const nextStep = async () => {
    if (!brandId) return;
    
    const currentFieldName = keyFields[currentStep][0] as keyof Brand;
    const currentValue = watch(currentFieldName);
    
    if (!currentValue || (typeof currentValue === 'string' && currentValue.trim() === '')) {
      // Set error state and trigger visual feedback
      setFieldError(`${currentFieldMetadata[1].name} is required before you can continue.`);
      setIsShaking(true);
      
      // Focus the input field
      const input = document.querySelector(`[name="${currentFieldName}"]`) as HTMLElement;
      if (input) {
        input.focus();
      }
      
      // Clear shake animation after animation completes
      setTimeout(() => setIsShaking(false), 500);
      return;
    }

    // Clear any existing error
    setFieldError('');

    try {
      // Include all fields in database update
      const updateData = {
        ...fieldValues,
        [currentFieldName]: currentValue,
        // Always set status to ACTIVE if this is the final step
        status: currentStep >= keyFields.length - 1 ? 'ACTIVE' : 'DRAFT'
      } as Partial<Brand>;

      // If URL field is being updated, also update default_landing_page
      if (currentFieldName === 'url' && currentValue) {
        updateData.default_landing_page = currentValue as string;
      }

      // If we're keeping the brand as DRAFT, check for conflicts first
      if (updateData.status === 'DRAFT') {
        // Try to get the current brand to see if it exists and what status it has
        const { data: currentBrand, error: fetchError } = await supabase
          .from('brands')
          .select('status')
          .eq('id', brandId)
          .single();
          
        if (fetchError) {
          if (fetchError.code === 'PGRST116') { // Record not found
            throw new Error('Brand not found');
          }
          throw fetchError;
        }
        
        // If the brand was previously ACTIVE and we're trying to set it back to DRAFT,
        // this could cause a constraint violation if another DRAFT brand exists
        if (currentBrand.status === 'ACTIVE') {
          // First check if another DRAFT brand exists for this account
          const { data: session } = await supabase.auth.getSession();
          if (!session || !session.session) {
            throw new Error('No active session');
          }
          
          const { data: existingDrafts } = await supabase
            .from('brands')
            .select('id')
            .eq('status', 'DRAFT')
            .eq('account_id', session.session.user.id);
            
          if (existingDrafts && existingDrafts.length > 0) {
            // If another draft exists, use ACTIVE status instead
            // This prevents the constraint violation
            updateData.status = 'ACTIVE';
          }
        }
      }
      
      // Now update the brand with potentially modified status
      const { error } = await supabase
        .from('brands')
        .update(updateData)
        .eq('id', brandId);

      if (error) {
        // If we still get a constraint violation, handle it by changing status to ACTIVE
        if (error.code === '23505' && error.message.includes('idx_one_draft_brand_per_account')) {
          console.log('Constraint violation detected, changing status to ACTIVE');
          // Update with ACTIVE status instead
          updateData.status = 'ACTIVE';
          const { error: retryError } = await supabase
            .from('brands')
            .update(updateData)
            .eq('id', brandId);
            
          if (retryError) throw retryError;
        } else {
          throw error;
        }
      }

      // Update local state with all fields
      setFieldValues(updateData);

      // Move to next step
      const nextStepIndex = currentStep + 1;
      
      // Check if this was the final step
      const isFinalStep = currentStep >= keyFields.length - 1;
      
      if (isFinalStep) {
        console.log('Final step reached, calling onComplete callback');
        console.log('Brand data at completion:', updateData);
        console.log('skipBilling value in BrandWizardStep:', skipBilling);
        // Ensure brand is ACTIVE
        if (updateData.status !== 'ACTIVE') {
          console.log('Ensuring brand is ACTIVE before completion');
          const { error: activationError } = await supabase
            .from('brands')
            .update({ status: 'ACTIVE' })
            .eq('id', brandId);
            
          if (activationError) {
            console.error('Error activating brand:', activationError);
            return;
          }
          updateData.status = 'ACTIVE';
        }
        // Update preview one final time
        onBrandUpdate(updateData);
        // Call onComplete to move to success step
        onComplete();
        return;
      }

      if (nextStepIndex < keyFields.length) {
        // Set the form value for the next field from our stored values
        const nextFieldName = keyFields[nextStepIndex][0] as keyof Brand;
        const nextValue = updateData[nextFieldName];
        if (nextValue !== undefined) {
          setValue(nextFieldName, nextValue);
        }
      }

      setCurrentStep(nextStepIndex);

      // For preview, show fields up to the next step
      const visibleFields = keyFields
        .slice(0, nextStepIndex + 1)
        .reduce((acc, [fieldName]) => {
          const value = updateData[fieldName as keyof Brand];
          if (value !== undefined && value !== null && value !== '') {
            acc[fieldName as keyof Brand] = value as any;
          }
          return acc;
        }, {} as Partial<Brand>);

      // Update preview with visible fields only
      onBrandUpdate(visibleFields);
    } catch (error) {
      console.error('Error updating brand:', error);
    }
  }

  // Clear error when user starts typing
  useEffect(() => {
    if (fieldError) {
      setFieldError('');
    }
  }, [currentFieldValue]);

  const prevStep = () => {
    if (currentStep > 0) {
      // Save current field value to state
      const currentFieldName = keyFields[currentStep][0] as keyof Brand;
      const currentValue = watch(currentFieldName);
      if (currentValue) {
        const updatedValues = {
          ...fieldValues,
          [currentFieldName]: currentValue
        };
        setFieldValues(updatedValues);
      }

      // Move to previous step
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);

      // Set the form value for the previous field from our stored values
      const prevFieldName = keyFields[prevStep][0] as keyof Brand;
      const prevValue = fieldValues[prevFieldName];
      if (prevValue !== undefined) {
        setValue(prevFieldName, prevValue);
      }

      // Show fields up to the previous step
      const visibleFields = keyFields
        .slice(0, prevStep + 1)
        .reduce((acc, [fieldName]) => {
          const value = fieldValues[fieldName as keyof Brand];
          if (value !== undefined && value !== null && value !== '') {
            acc[fieldName as keyof Brand] = value as any;
          }
          return acc;
        }, {} as Partial<Brand>);

      // Update preview with visible fields
      onBrandUpdate(visibleFields);
    } else {
      // If we're at the first brand step, go back to welcome
      onPrevious();
    }
  }

  // Focus input field when step changes
  useEffect(() => {
    // Get the current field's input element
    const input = document.querySelector(`[name="${keyFields[currentStep]?.[0]}"]`) as HTMLElement;
    if (input) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        input.focus();
      }, 100);
    }
  }, [currentStep, keyFields]);

  const currentFieldMetadata = keyFields[currentStep];
  if (!currentFieldMetadata) return null;

  const [fieldName, metadata] = currentFieldMetadata;
  const fieldKey = fieldName as keyof Brand;

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default form submission
      nextStep();
    }
  };

  // Get field icon based on field name
  const getFieldIcon = (fieldName: string) => {
    const iconMap: Record<string, any> = {
      brand_name: User,
      elevator_pitch: MessageSquare,
      target_market: Target,
      url: Globe,
      brand_personality: Palette,
      customer_avatar: Users,
      default: Lightbulb
    };
    return iconMap[fieldName] || iconMap.default;
  };

  const FieldIcon = getFieldIcon(fieldName);
  const progress = ((currentStep + 1) / keyFields.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      {/* Enhanced Container */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-3xl border border-blue-200/50 shadow-xl">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-200/20 to-transparent rounded-full translate-y-12 -translate-x-12" />

          <div className="relative p-8">
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <FieldIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-600">
                      Step {currentStep + 1} of {keyFields.length}
                    </p>
                    <div className="flex items-center gap-2">
                      {keyFields.slice(0, currentStep).map((_, index) => (
                        <CheckCircle key={index} className="h-4 w-4 text-green-500" />
                      ))}
                      <div className="h-4 w-4 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                        <div className="h-2 w-2 bg-white rounded-full" />
                      </div>
                      {keyFields.slice(currentStep + 1).map((_, index) => (
                        <div key={currentStep + 1 + index} className="h-4 w-4 rounded-full border-2 border-gray-200" />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {Math.round(progress)}%
                  </div>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </div>
              </div>
              <Progress value={progress} className="h-2 bg-blue-100" />
            </div>

            {/* Enhanced Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-3">
                {metadata.name}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {metadata.howItsUsed}
              </p>
            </div>

            {/* Enhanced Input Section */}
            <div className={cn(
              "relative p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-blue-200/30 transition-all duration-300",
              fieldError && "border-red-300",
              isShaking && "animate-shake"
            )}>
              {metadata.inputControl === 'Textarea' ? (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {metadata.name}
                  </label>
                  <Textarea
                    {...register(fieldKey)}
                    placeholder={metadata.example.toString()}
                    className={cn(
                      "min-h-[200px] bg-white/80 backdrop-blur-sm border-blue-200/50 focus:border-blue-400 focus:ring-2 focus:ring-blue-200 resize-none transition-all duration-200",
                      fieldError && "border-red-300 focus:border-red-400 focus:ring-red-200"
                    )}
                    onKeyDown={(e) => {
                      // For textarea, only proceed if Ctrl+Enter or Cmd+Enter is pressed
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        nextStep();
                      }
                    }}
                  />
                  {fieldError && (
                    <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-red-50 to-red-100/50 rounded-lg border border-red-200 animate-in slide-in-from-top-2">
                      <AlertCircle size={16} className="text-red-500" />
                      <span className="text-red-700 text-sm font-medium">{fieldError}</span>
                    </div>
                  )}
                </div>
              ) : metadata.options && metadata.options.length > 0 ? (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {metadata.name}
                  </label>
                  <ButtonGroup
                    value={watch(fieldKey) || ''}
                    onValueChange={(value) => setValue(fieldKey, value)}
                    className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                  >
                    {metadata.options.map((option) => (
                      <ButtonGroupItem
                        key={option}
                        value={option}
                        className={cn(
                          "relative p-4 text-left transition-all duration-200 hover:shadow-md",
                          "border-2 rounded-xl font-medium",
                          watch(fieldKey) === option
                            ? "border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-700 shadow-md"
                            : "border-gray-200 bg-white/80 hover:border-blue-300 hover:bg-blue-50/50"
                        )}
                      >
                        {option}
                        {watch(fieldKey) === option && (
                          <CheckCircle className="absolute top-2 right-2 h-4 w-4 text-blue-600" />
                        )}
                      </ButtonGroupItem>
                    ))}
                  </ButtonGroup>
                  {fieldError && (
                    <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-red-50 to-red-100/50 rounded-lg border border-red-200 animate-in slide-in-from-top-2">
                      <AlertCircle size={16} className="text-red-500" />
                      <span className="text-red-700 text-sm font-medium">{fieldError}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {metadata.name}
                  </label>
                  <Input
                    {...register(fieldKey)}
                    type="text"
                    placeholder={metadata.example.toString()}
                    className={cn(
                      "h-12 bg-white/80 backdrop-blur-sm border-blue-200/50 focus:border-blue-400 focus:ring-2 focus:ring-blue-200 transition-all duration-200",
                      fieldError && "border-red-300 focus:border-red-400 focus:ring-red-200"
                    )}
                    onKeyDown={handleKeyDown}
                  />
                  {fieldError && (
                    <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-red-50 to-red-100/50 rounded-lg border border-red-200 animate-in slide-in-from-top-2">
                      <AlertCircle size={16} className="text-red-500" />
                      <span className="text-red-700 text-sm font-medium">{fieldError}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Enter to continue hint - hide for Textarea since Enter creates a new line */}
              {metadata.inputControl !== 'Textarea' && (
                <div className="flex justify-center mt-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Press</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded border text-xs">Enter</kbd>
                    <span>to continue</span>
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Best Practices Alert */}
            {metadata.bestPractices && (
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 backdrop-blur-sm rounded-2xl border border-blue-200/30 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                    <Lightbulb className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-2">Best Practices</h4>
                    <p className="text-blue-800 text-sm leading-relaxed">
                      {metadata.bestPractices}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced RainbowButton Container */}
            {metadata.allowAiGeneration && (
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50/80 to-pink-50/80 backdrop-blur-sm rounded-2xl border border-purple-200/30 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shadow-sm">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-900">AI Assistant</h4>
                    <p className="text-purple-700 text-sm">Let Brand Genius help you craft the perfect response</p>
                  </div>
                </div>
                <RainbowButton
                  onClick={() => {
                    setCurrentAIField(fieldKey);
                    const currentValue = watch(fieldKey);

                    if (currentValue && String(currentValue).trim() !== '') {
                      // If the field already has a value, show the choice modal
                      setShowChoiceModal(true);
                    } else {
                      // Otherwise show the AI dialog directly
                      setIsFeedbackMode(false);
                      setShowAIDialog(true);
                    }
                  }}
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <AutoAwesome className="mr-2" />
                  Ask Brand Genius
                </RainbowButton>
              </div>
            )}
            {/* Enhanced Navigation Buttons */}
            <div className="flex justify-between items-center pt-8 mt-8 border-t border-blue-200/30">
              <Button
                variant="outline"
                onClick={prevStep}
                className="px-6 py-3 bg-white/80 backdrop-blur-sm border-blue-200/50 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <ChevronLeft className="mr-2" size={16} />
                Previous
              </Button>

              <Button
                onClick={nextStep}
                disabled={isSubmitting}
                className={cn(
                  "px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 border-0",
                  isShaking && "animate-bounce"
                )}
              >
                {currentStep === keyFields.length - 1 ? 'Complete Setup' : 'Next Step'}
                <ChevronRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced AI Generate Dialog */}
      {showAIDialog && (
        <AIGenerateDialog
          fieldName={currentAIField}
          brand={watch() as Brand}
          defaultInstructions={metadata.bestPractices}
          fieldMetadata={metadata}
          existingValue={isFeedbackMode ? watch(currentAIField) : undefined}
          initialFeedback={isFeedbackMode ? brandGeniusFeedback : ''}
          onApply={(field: keyof Brand, content: string) => {
            setValue(field, content);
            onBrandUpdate({ [field]: content });
            setShowAIDialog(false);
            setIsFeedbackMode(false);
            setBrandGeniusFeedback('');
          }}
          onClose={() => {
            setShowAIDialog(false);
            setIsFeedbackMode(false);
            setBrandGeniusFeedback('');
          }}
        />
      )}
      
      {/* Enhanced Choice Modal */}
      {showChoiceModal && (
        <Dialog open={showChoiceModal} onOpenChange={setShowChoiceModal}>
          <DialogContent className="sm:max-w-[550px] p-8 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 backdrop-blur-sm border border-blue-200/50 shadow-2xl">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative">
              <DialogHeader className="text-center mb-6">
                <div className="mx-auto mb-4 p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg w-fit">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 bg-clip-text text-transparent">
                  How would you like Brand Genius to help?
                </DialogTitle>
                <DialogDescription className="text-gray-600 text-base mt-2">
                  Choose how you want Brand Genius to assist with this field.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <Button
                  onClick={() => {
                    setShowChoiceModal(false);
                    setIsFeedbackMode(false);
                    setShowAIDialog(true);
                  }}
                  className="w-full p-6 h-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 text-left"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-white/20 rounded-lg mr-4">
                      <AutoAwesome className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-semibold text-lg">Generate New Content</div>
                      <div className="text-blue-100 text-sm">Let Brand Genius create a fresh response for you</div>
                    </div>
                  </div>
                </Button>

                <Button
                  onClick={() => {
                    setShowChoiceModal(false);
                    setIsFeedbackMode(true);
                    setBrandGeniusFeedback('');
                    setShowAIDialog(true);
                  }}
                  variant="outline"
                  className="w-full p-6 h-auto bg-white/80 backdrop-blur-sm border-purple-200 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 text-left"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg mr-4">
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-lg text-gray-900">Improve Current Answer</div>
                      <div className="text-gray-600 text-sm">Give feedback to enhance your existing response</div>
                    </div>
                  </div>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
} 